import clsxm from '@/lib/clsxm';

import { CheckGlyph, tone } from '@/components/agents/primitives';
import { CONNECTOR_ICONS } from '@/components/Chat/connectors/icons';

import Feature from './Feature';
import {
  BRAND,
  CAST,
  type CastMember,
  CUSTOMER,
  FLOAT_CARD,
  Portrait,
  useEmployeesT,
  WINDOW,
} from './kit';

type Tag = 'Content' | 'Research' | 'Design' | 'Urgent';

const TAG_STYLE: Record<Tag, string> = {
  Content: 'bg-sky-400/15 text-sky-200',
  Research: 'bg-violet-400/15 text-violet-200',
  Design: 'bg-pink-400/15 text-pink-200',
  Urgent: 'bg-rose-400/15 text-rose-200',
};

type BoardTask = {
  number: number;
  title: string;
  member: CastMember;
  tag: Tag;
  review?: boolean;
};

/** Tasks: every piece of work has an owner, a status and a review step. */
export function EmployeesTasks() {
  const { t } = useEmployeesT();

  const columns: { label: string; tasks: BoardTask[] }[] = [
    {
      label: t('To do'),
      tasks: [
        {
          number: 17,
          title: t('Instagram carousel for Bloom'),
          member: CAST.leo,
          tag: 'Content',
        },
      ],
    },
    {
      label: t('In progress'),
      tasks: [
        {
          number: 16,
          title: t('Launch-week sales report'),
          member: CAST.nova,
          tag: 'Research',
        },
      ],
    },
    {
      label: t('In review'),
      tasks: [
        {
          number: 14,
          title: t('Spring launch banner'),
          member: CAST.mira,
          tag: 'Design',
          review: true,
        },
        {
          number: 15,
          title: t('Launch email'),
          member: CAST.ella,
          tag: 'Urgent',
        },
      ],
    },
    {
      label: t('Done'),
      tasks: [
        {
          number: 12,
          title: t('Bloom product page copy'),
          member: CAST.leo,
          tag: 'Content',
        },
        {
          number: 11,
          title: t('Competitor price check'),
          member: CAST.nova,
          tag: 'Research',
        },
      ],
    },
  ];

  return (
    <Feature
      id='tasks'
      eyebrow={t('Tasks')}
      title={t('Every task, tracked.')}
      tail={t('From to-do to done.')}
      lead={t(
        'Turn any message into a task, or let the lead file them. Each task has one owner, a status and a thread that holds the work.',
      )}
      points={[
        t('A board for the whole team, and one for what needs you'),
        t(
          'Delivered work waits for your review: mark it done or ask for changes',
        ),
        t('A task that stalls gets a reminder, and you hear if it stays stuck'),
      ]}
      description={t(
        'A task board with four columns: To do, In progress, In review and Done. The banner the designer delivered waits in review with Mark done and Ask for changes buttons.',
      )}
      visual={
        <div className={clsxm(WINDOW, 'p-3 sm:p-4')}>
          <div className='flex items-center justify-between px-1 pb-3'>
            <p className='text-[14px] font-semibold'>{t('Tasks')}</p>
            <p className='text-xs text-white/45'>
              {BRAND} · # {t('Spring launch')}
            </p>
          </div>
          <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
            {columns.map((column) => (
              <div
                key={column.label}
                className='min-w-0 rounded-xl bg-white/[0.03] p-2 ring-1 ring-inset ring-white/[0.05]'
              >
                <p className='flex items-center justify-between px-1 pb-2 text-[11px] font-semibold text-white/55'>
                  <span className='truncate'>{column.label}</span>
                  <span className='tabular-nums text-white/35'>
                    {column.tasks.length}
                  </span>
                </p>
                <ul className='space-y-2'>
                  {column.tasks.map((task) => (
                    <li
                      key={task.number}
                      className={clsxm(
                        'rounded-lg bg-[#1b1b25] p-2.5 ring-1 ring-inset',
                        task.review ? 'ring-amber-400/50' : 'ring-white/[0.06]',
                      )}
                    >
                      <p className='line-clamp-2 text-[12px] font-medium leading-snug text-white/90'>
                        {task.title}
                      </p>
                      <p className='mt-2 flex items-center gap-1.5'>
                        <Portrait src={task.member.photo} size={18} />
                        <span
                          className='text-[10px] tabular-nums text-white/40'
                          dir='ltr'
                        >
                          #{task.number}
                        </span>
                        <span
                          className={clsxm(
                            'ms-auto truncate rounded px-1.5 text-[10px] font-semibold leading-4',
                            TAG_STYLE[task.tag],
                          )}
                        >
                          {t(task.tag)}
                        </span>
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className='mt-3 flex flex-col gap-3 rounded-xl bg-amber-400/10 p-3 ring-1 ring-inset ring-amber-400/25 sm:flex-row sm:items-center'>
            <p className='flex min-w-0 flex-1 items-center gap-2.5 text-[13px]'>
              <Portrait src={CAST.mira.photo} size={24} />
              <span className='min-w-0 truncate text-white/85'>
                {t('{{name}} delivered #{{number}}. Is it done?', {
                  name: CAST.mira.name,
                  number: 14,
                })}
              </span>
            </p>
            <span className='flex flex-none gap-2 text-xs font-semibold'>
              <span className='rounded-full px-3 py-1.5 ring-1 ring-inset ring-white/20'>
                {t('Ask for changes')}
              </span>
              <span className='flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[#10101c]'>
                <CheckGlyph className='h-3 w-3' />
                {t('Mark done')}
              </span>
            </span>
          </div>
        </div>
      }
    />
  );
}

/** Approvals: nothing leaves your business without a yes. */
export function EmployeesApprovals() {
  const { t } = useEmployeesT();
  const policy = [
    t('Ask before sending messages'),
    t('Ask before changing calendars'),
    t('Ask before writing to other apps'),
  ];

  return (
    <Feature
      id='approvals'
      reverse
      stage='indigo'
      eyebrow={t('Approvals')}
      title={t('Nothing goes out without your OK.')}
      lead={t(
        'Emails, calendar invites and changes in your apps wait for your approval. You see exactly what will be sent before it goes.',
      )}
      points={[
        t('Approve as is, edit it first, or reject it with a note'),
        t('Away from your desk? Approvals wait for a week, and we email you'),
        t('Choose for each employee what needs your approval'),
      ]}
      description={t(
        'An approval card: the email specialist wants to send an email to a customer. It shows the recipient, the subject and the message, with Reject, Edit and Approve buttons. Next to it, the employee’s approval settings, all set to ask first.',
      )}
      visual={
        <div className='relative grid gap-4 lg:grid-cols-[1fr_210px] lg:items-start'>
          <div className={clsxm(FLOAT_CARD, 'min-w-0 p-4 sm:p-5')}>
            <div className='flex items-center gap-3'>
              <Portrait
                src={CAST.ella.photo}
                size={36}
                status='waiting'
                ringClass='ring-white dark:ring-[#16151f]'
              />
              <div className='min-w-0 flex-1'>
                <p className='truncate text-[14px] font-semibold'>
                  {t('Send an email to {{name}}', { name: CUSTOMER.name })}
                </p>
                <p className={clsxm('truncate text-xs', tone.quiet)}>
                  {CAST.ella.name} · {t('Waiting for you')}
                </p>
              </div>
              <span className='flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#f3f3f7] dark:bg-white'>
                {CONNECTOR_ICONS.gmail('h-4 w-4')}
              </span>
            </div>

            <dl className='mt-4 divide-y divide-[#0f0f28]/[0.06] rounded-xl bg-[#f6f6f9] text-[13px] dark:divide-white/[0.06] dark:bg-white/[0.04]'>
              <div className='flex gap-3 px-3.5 py-2.5'>
                <dt className={clsxm('w-16 flex-none', tone.quiet)}>
                  {t('To')}
                </dt>
                <dd className='min-w-0 truncate font-medium'>
                  {CUSTOMER.name} · {CUSTOMER.company}
                </dd>
              </div>
              <div className='flex gap-3 px-3.5 py-2.5'>
                <dt className={clsxm('w-16 flex-none', tone.quiet)}>
                  {t('Subject')}
                </dt>
                <dd className='min-w-0 truncate font-medium'>
                  {t('Bloom is here: spring stock for Luma')}
                </dd>
              </div>
              <div className={clsxm('px-3.5 py-3 leading-relaxed', tone.body)}>
                <p>{t('Hi Ana,')}</p>
                <p className='mt-1.5'>
                  {t(
                    'Our new spring scent Bloom launches on Monday. As one of our first stockists, you can order before anyone else. Shall I reserve your usual 24 bottles?',
                  )}
                </p>
              </div>
            </dl>

            <div className='mt-4 grid grid-cols-3 gap-2 text-[13px] font-semibold'>
              <span className='rounded-full py-2.5 text-center ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/15'>
                {t('Reject')}
              </span>
              <span className='rounded-full py-2.5 text-center ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/15'>
                {t('Edit')}
              </span>
              <span className='rounded-full bg-primary-600 py-2.5 text-center text-white'>
                {t('Approve')}
              </span>
            </div>
          </div>

          <div className='min-w-0 rounded-2xl bg-[#0d0c1f] p-4 text-white shadow-[0_30px_70px_-25px_rgba(8,6,40,0.8)] ring-1 ring-white/10 lg:mt-10'>
            <p className='text-xs font-semibold text-white/60'>
              {t('{{name}}’s approvals', { name: CAST.ella.name })}
            </p>
            <ul className='mt-3 space-y-3'>
              {policy.map((label) => (
                <li
                  key={label}
                  className='flex items-center justify-between gap-3 text-[12px] leading-snug text-white/85'
                >
                  <span>{label}</span>
                  <span className='relative h-5 w-9 flex-none rounded-full bg-primary-500'>
                    <span className='absolute end-0.5 top-0.5 h-4 w-4 rounded-full bg-white' />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
    />
  );
}

type Routine = {
  member: CastMember;
  name: string;
  schedule: string;
  last: string;
};

/** Routines: standing jobs that run on a schedule, with a run history. */
export function EmployeesRoutines() {
  const { t } = useEmployeesT();
  const routines: Routine[] = [
    {
      member: CAST.nova,
      name: t('Daily business brief'),
      schedule: t('Weekdays at 8:00'),
      last: t('Today, 8:00'),
    },
    {
      member: CAST.maya,
      name: t('Draft replies to new support emails'),
      schedule: t('Every 15 minutes'),
      last: t('5 min ago'),
    },
    {
      member: CAST.atlas,
      name: t('Weekly team status'),
      schedule: t('Mondays at 9:00'),
      last: t('Monday, 9:00'),
    },
    {
      member: CAST.ella,
      name: t('Monthly newsletter draft'),
      schedule: t('1st of the month at 9:00'),
      last: t('October 1'),
    },
  ];

  return (
    <Feature
      id='routines'
      stage='ink'
      halftoneAt='100% 100%'
      eyebrow={t('Routines')}
      title={t('They work while you sleep.')}
      lead={t(
        'Give each employee standing jobs: a morning brief, an inbox sweep, a weekly report. Ask for one in plain words and it runs on schedule.',
      )}
      points={[
        t('Runs on our servers, even with your laptop closed'),
        t('Results land in your chat, and every run is in the history'),
        t('Run any routine now to see what it does'),
      ]}
      description={t(
        'A list of routines: a daily business brief on weekdays at 8:00, support email drafts every 15 minutes, a weekly team status on Mondays and a monthly newsletter draft. Above it, a morning notification from the analyst with this week’s orders.',
      )}
      visual={
        <div className='space-y-4'>
          <div className='ms-auto max-w-[330px] rounded-2xl bg-white/[0.09] p-3.5 text-white ring-1 ring-inset ring-white/15 sm:me-4'>
            <p className='flex items-center gap-2.5'>
              <Portrait src={CAST.nova.photo} size={28} />
              <span className='min-w-0 flex-1'>
                <span className='block truncate text-[13px] font-semibold'>
                  {CAST.nova.name} · {t('Daily business brief')}
                </span>
                <span className='block text-[11px] text-white/50'>
                  {t('Today, 8:00')}
                </span>
              </span>
            </p>
            <p className='mt-2.5 text-[13px] leading-snug text-white/85'>
              {t('Orders are up 18% this week. Bloom pre-orders: 212.')}
            </p>
          </div>

          <div className={clsxm(WINDOW, 'p-3 sm:p-4')}>
            <p className='flex items-center justify-between px-1.5 pb-2 text-[14px] font-semibold'>
              {t('Routines')}
              <span className='text-xs font-medium text-white/45'>
                {t('All on schedule')}
              </span>
            </p>
            <ul className='divide-y divide-white/[0.06]'>
              {routines.map((routine) => (
                <li
                  key={routine.name}
                  className='flex items-center gap-3 px-1.5 py-3'
                >
                  <Portrait src={routine.member.photo} size={32} />
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-[13px] font-medium text-white/90'>
                      {routine.name}
                    </span>
                    <span className='block truncate text-[11px] text-white/45'>
                      {routine.member.name} · {routine.schedule}
                    </span>
                  </span>
                  <span className='hidden flex-none items-center gap-1.5 text-[11px] text-white/50 sm:flex'>
                    <span className='flex h-4 w-4 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300'>
                      <CheckGlyph className='h-2.5 w-2.5' />
                    </span>
                    {routine.last}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
    />
  );
}
